import React, { useState, useEffect } from 'react'
import { invalidateReviewCaches } from '../utils/homeFeedCache'
import { useParams, useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import ReviewImageSlider from '../components/ReviewImageSlider'
import PlaceAddButton from '../components/PlaceAddButton'
import BookmarkButton from '../components/BookmarkButton'
import ReviewLikeCount from '../components/ReviewLikeCount'
import ReviewCommentCount from '../components/ReviewCommentCount'
import TextListBottomSheet from '../components/TextListBottomSheet'
import { reviewApi } from '../api/review'
import { VisitType, getVisitTypeDisplayLabel } from '../constants/visitType'
import LoadingSpinner from '../components/LoadingSpinner'
import { toastUtil } from '../utils/toast'
import { deleteReviewMessage } from '../utils/reviewDeleteMessage'
import { dialogUtil } from '../utils/dialog'
import { pageCache } from '../utils/pageCache'
import {
  photoViewerCacheKey,
  type PhotoViewerData,
} from '../utils/photoViewerCache'
import { useAuthStore } from '../stores/authStore'

const ReviewPhotoDetail: React.FC = () => {
  const { reviewId, imageIndex } = useParams<{
    reviewId: string
    imageIndex: string
  }>()
  const navigate = useNavigate()
  // 스와이프로 돌아왔을 때 이전 내용을 그대로 보여준다 (깜빡임 방지)
  const cacheKey = photoViewerCacheKey(reviewId ?? '')
  const [review, setReview] = useState<PhotoViewerData | null>(() =>
    pageCache.get(cacheKey)
  )
  const [isLoading, setIsLoading] = useState(!pageCache.has(cacheKey))
  /*
   * 몇 번째 사진인지는 처음 그릴 때부터 알고 있어야 한다.
   *
   * 0으로 시작해 useEffect에서 바꾸면, 화면이 이미 1번 사진으로 그려진 뒤에
   * 3번으로 옮겨간다. 그 이동이 슬라이드 애니메이션을 타서 1·2번이 스쳐
   * 지나갔다 — '휘리릭' 넘어가 보이던 것이 이것이다.
   */
  const [currentIndex, setCurrentIndex] = useState(() => {
    const n = imageIndex ? parseInt(imageIndex, 10) : 0
    return Number.isFinite(n) && n >= 0 ? n : 0
  })
  const [
    isReviewActionBottomSheetOpen,
    setIsReviewActionBottomSheetOpen,
  ] = useState(false)
  const { userId: currentUserId } = useAuthStore()

  useEffect(() => {
    const loadReview = async () => {
      if (!reviewId) {
        navigate(-1)
        return
      }

      try {
        // 캐시로 이미 그려져 있으면 로딩 화면으로 덮지 않는다
        if (!pageCache.has(cacheKey)) {
          setIsLoading(true)
        }
        const response = await reviewApi.getReviewDetail(parseInt(reviewId))
        const reviewData = response.review

        // 이미지 URL 배열 추출 (photos 배열에서 preview 추출)
        const imageUrls = reviewData.photos?.map((photo) => photo.preview) || []
        // 사진이 아직 안 그려진 순간에 대신 보일 작은 판 (없으면 null)
        const imageBlurs = reviewData.photos?.map((photo) => photo.blur ?? null) || []

        const rating =
          reviewData.score != null
            ? Math.min(10, Math.max(0, Number(reviewData.score)))
            : 0

        const next: PhotoViewerData = {
          placeName: reviewData.place?.name || '',
          placeAddress: reviewData.place?.address || '',
          placeId: reviewData.place_id.toString(),
          rating: rating,
          visitDate: reviewData.visit_date,
          createdAt: reviewData.created_at,
          visitType: reviewData.visit_type as VisitType,
          visitNumber: reviewData.visit_number,
          reviewText: reviewData.review_text ?? null,
          authorName: reviewData.username ?? reviewData.nickname ?? null,
          images: imageUrls,
          imageBlurs: imageBlurs,
          // 댓글 수는 따로 받아온다. 사진을 보러 들어온 사람을 숫자 하나
          // 때문에 빈 화면 앞에 세워둘 이유가 없다 — 두 번째 왕복을 기다리는
          // 동안 화면 전체가 크림색이라 흰 화면이 번쩍이는 것으로 보였다.
          commentCount: review?.commentCount ?? 0,
          likeCount: reviewData.like_count || 0,
          isLiked: reviewData.is_liked || false,
          hasUserReview: reviewData.has_user_review,
          isBookmarked: reviewData.is_bookmarked,
          userId: reviewData.user_id ?? null,
        }

        pageCache.set(cacheKey, next)
        setReview(next)

        // 사진을 먼저 띄운 뒤 조용히 채운다. 실패해도 사진 보는 데는 지장이 없다.
        reviewApi
          .getComments(parseInt(reviewId))
          .then((res) => {
            const withCount = { ...next, commentCount: res.comments.length }
            pageCache.set(cacheKey, withCount)
            setReview((prev) => (prev === next ? withCount : prev))
          })
          .catch(() => {})
      } catch (error) {
        console.error('리뷰 정보 로드 실패:', error)
        toastUtil.error('기록을 불러오는데 실패했습니다')
        navigate(-1)
      } finally {
        setIsLoading(false)
      }
    }

    loadReview()
  }, [reviewId, navigate])

  const handleIndexChange = (index: number) => {
    setCurrentIndex(index)
  }

  const isOwnReview = !!(
    review &&
    currentUserId &&
    review.userId === currentUserId
  )

  const handleOpenReviewActionBottomSheet = () => {
    setIsReviewActionBottomSheetOpen(true)
  }

  const handleCloseReviewActionBottomSheet = () => {
    setIsReviewActionBottomSheetOpen(false)
  }

  const handleAuthorClick = () => {
    if (!review?.userId) return
    // 내 기록이면 남의 프로필 화면 대신 마이로 보낸다 (다른 화면과 같은 규칙)
    if (review.userId === currentUserId) {
      navigate('/main-my')
      return
    }
    navigate(`/user/${review.userId}/profile`)
  }

  const handleEditReview = () => {
    if (!review) return
    navigate(`/store-review/${review.placeId}`)
  }

  const handleDeleteReview = async () => {
    if (!reviewId || !review) return

    const confirmed = await dialogUtil.confirm({
      message: deleteReviewMessage(review.visitNumber),
      confirmText: '삭제',
      cancelText: '취소',
    })

    if (!confirmed) return

    try {
      await reviewApi.deleteReview(parseInt(reviewId))
      // 지운 기록이 홈 피드·랭킹 캐시에 남아 있으면 계속 보인다. 버린다.
      invalidateReviewCaches()
      toastUtil.success('기록이 삭제되었습니다')
      navigate(-1)
    } catch (error) {
      console.error('리뷰 삭제 실패:', error)
      toastUtil.error('기록 삭제 중 오류가 발생했습니다')
    }
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    return `${year}년 ${month}월 ${day}일`
  }

  if (isLoading || !review) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-ivoryKey">
        <LoadingSpinner />
      </div>
    )
  }

  if (!review.images || review.images.length === 0) {
    return (
      <div className="w-full h-full flex flex-col bg-ivoryKey">
        <AppBar title={review.placeName} />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-style-body2 text-textColorText2">
            이미지를 불러오는데 실패했습니다.
          </p>
        </div>
      </div>
    )
  }

  return (
    /*
     * 이 화면 전체에서 '밀어서 뒤로가기'를 끈다.
     *
     * 오른쪽으로 미는 손짓이 두 가지를 뜻한다 — 이전 사진(캐러셀)과 뒤로가기다.
     * 뒤로가기가 걸리면 화면 전체를 덮는 크림색(#FAF6EF) 오버레이가 깔렸다가
     * 손을 떼면 되돌아간다. 그 왕복이 사용자가 본 '흰색 번쩍'이었다.
     *
     * 예전에는 캐러셀에만 차단 표시를 뒀는데, 그 표시는 손가락이 닿은 지점에서
     * 위로 올라가며 찾는다. 사진 아래 본문·좋아요·댓글칸에서 밀면 캐러셀을
     * 만나지 못해 그대로 뒤로가기가 걸렸다. 첫 장에서는 표시 자체가 없어
     * 사진 위에서 밀어도 걸렸다.
     *
     * 이 화면은 상단에 뒤로가기 버튼이 있으므로 스와이프로 나갈 길이 막혀도
     * 갇히지 않는다.
     */
    // 전면 차단을 걷는다. 예전에 깜빡임 원인을 뒤로가기 제스처로 잘못 짚어
    // 이 화면 전체에서 막아뒀는데, 진짜 원인은 칸의 타일이 늦게 그려지는
    // 것이었다. 막아둘 이유가 없어졌고, 첫 사진에서는 뒤로가기가 되어야 한다.
    <div className="w-full h-full flex flex-col bg-ivoryKey">
      <div className="app-container flex-1 flex flex-col overflow-y-auto scrollbar-hide">
        <AppBar
          title={review.placeName}
          isBottomBorder
          rightElement={
            isOwnReview ? (
              <button
                type="button"
                onClick={handleOpenReviewActionBottomSheet}
                className="font-pretendard font-bold text-16px leading-24px text-[#6B5E46] pr-20px"
              >
                수정
              </button>
            ) : undefined
          }
        />

        {/* 이미지 슬라이드 */}
        <ReviewImageSlider
          images={review.images}
          blurs={review.imageBlurs}
          initialIndex={currentIndex}
          onIndexChange={handleIndexChange}
        />

        {/* 하단 정보 영역 */}
        <div className="w-full px-20px py-16px flex flex-col">
          <div className="w-full flex gap-8px">
            {/* 좌측 패널 */}
            <div className="flex-1 flex gap-12px">
              {/* 좋아요 */}
              {reviewId && (
                <ReviewLikeCount
                  reviewId={reviewId}
                  initialLikeCount={review.likeCount}
                  initialIsLiked={review.isLiked}
                />
              )}

              {/* 댓글 */}
              {/* 이 화면에는 입력칸이 없다. 자세히보기로 보내면서 댓글 쓰러 왔다고 알린다 */}
              <ReviewCommentCount
                commentCount={review.commentCount}
                onClick={() =>
                  navigate(`/review-detail/${reviewId}`, {
                    state: { focusComment: true },
                  })
                }
              />
            </div>

            {/* 우측 패널 */}
            <div className="flex gap-8px">
              {/* 리뷰 작성 버튼 */}
              <PlaceAddButton
                storeId={review.placeId}
                buttonSize={26}
                isAdded={review.hasUserReview}
              />

              {/* 북마크 버튼 */}
              <BookmarkButton
                storeId={review.placeId}
                buttonSize={26}
                isBookmarked={review.isBookmarked}
                placeName={review.placeName}
                placeAddress={review.placeAddress}
              />
            </div>
          </div>

          {/* 본문 텍스트(한줄평) — 있을 때만. 앞에 작성자 닉네임(굵게) + 공백 2칸(인스타 스타일) */}
          {review.reviewText && review.reviewText.trim() !== '' && (
            <p className="mt-16px w-full text-14px leading-150% text-textColorText2 whitespace-pre-line break-all">
              {review.authorName && (
                /*
                 * 이름을 누르면 프로필로 간다.
                 *
                 * 피드와 자세히보기에서는 프로필 사진 옆 이름이 눌리는데
                 * 이 화면만 <strong>이라 아무 반응이 없었다. 같은 이름이
                 * 어떤 화면에서는 눌리고 어떤 화면에서는 안 눌리면
                 * 눌러도 되는지 알 수 없다.
                 */
                <strong
                  className={`font-bold text-4B3621-80% ${
                    review.userId ? 'cursor-pointer' : ''
                  }`}
                  onClick={review.userId ? handleAuthorClick : undefined}
                >
                  {review.authorName}
                </strong>
              )}
              {review.authorName ? '\u00A0' : ''}
              {review.reviewText}
            </p>
          )}

          <div className="mt-20px w-full flex gap-8px">
            {/* 좌측: 날짜 */}
            <p className="flex-1 text-style-captionM text-textColorText3">
              {formatDate(review.visitDate || review.createdAt)}
            </p>

            {/* 우측: 서비스 유형 */}
            <p className="text-style-captionM text-textColorText3 text-right">
              {getVisitTypeDisplayLabel(review.visitType)}
            </p>
          </div>
        </div>
      </div>

      {/* 리뷰 수정/삭제 바텀 시트 */}
      <TextListBottomSheet
        isOpen={isReviewActionBottomSheetOpen}
        onClose={handleCloseReviewActionBottomSheet}
        items={[
          {
            text: '기록 수정',
            onClick: handleEditReview,
          },
          {
            text: '기록 삭제',
            onClick: handleDeleteReview,
          },
        ]}
      />
    </div>
  )
}

export default ReviewPhotoDetail
